/**
 * StripeConnectCallback Page
 * Handles return from Stripe Connect onboarding and verifies completion status
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionService } from '@/services/subscription.service';
import type { StripeAccountStatus } from '@/services/subscription.service';

type OnboardingStatus = 'checking' | 'complete' | 'incomplete' | 'error';

const StripeConnectCallback: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<OnboardingStatus>('checking');
  const [stripeStatus, setStripeStatus] = useState<StripeAccountStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryLoading, setRetryLoading] = useState(false);

  const checkOnboardingStatus = useCallback(async () => {
    setStatus('checking');
    setError(null);

    try {
      const result = await subscriptionService.getStripeStatus();
      setStripeStatus(result);

      if (result.charges_enabled && result.details_submitted) {
        setStatus('complete');
        // Auto-redirect to dashboard after 5 seconds on success
        setTimeout(() => {
          navigate('/provider/dashboard');
        }, 5000);
      } else {
        setStatus('incomplete');
      }
    } catch (err) {
      console.error('Failed to check Stripe status:', err);
      setError(err instanceof Error ? err.message : 'Failed to verify onboarding status');
      setStatus('error');
    }
  }, [navigate]);

  useEffect(() => {
    checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  const handleRetryOnboarding = async () => {
    setRetryLoading(true);
    try {
      const result = await subscriptionService.getStripeOnboardingLink();
      window.location.href = result.onboardingUrl;
    } catch (err) {
      console.error('Failed to restart onboarding:', err);
      setError(err instanceof Error ? err.message : 'Failed to restart onboarding');
      setRetryLoading(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/provider/dashboard');
  };

  // Checking status
  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-4">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
          </div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">Verifying Setup</h2>
          <p className="text-neutral-600">Checking your payment processing status...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (status === 'complete') {
    return (
      <div className="min-h-screen bg-neutral-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          {/* Success Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
            <svg
              className="w-12 h-12 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h1 className="text-3xl font-bold text-neutral-900 mb-4">
            Payment Setup Complete!
          </h1>
          <p className="text-lg text-neutral-600 mb-8">
            You can now receive deposit payments from marketplace jobs.
          </p>

          {/* Status Details */}
          <div className="bg-white rounded-lg border border-neutral-200 p-6 mb-8 text-left">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Verified Capabilities</h3>
            <ul className="space-y-3">
              <li className="flex items-center">
                <svg className="w-5 h-5 text-green-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-neutral-700">Accept card payments</span>
              </li>
              <li className="flex items-center">
                <svg className="w-5 h-5 text-green-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-neutral-700">Receive payouts to your bank</span>
              </li>
              <li className="flex items-center">
                <svg className="w-5 h-5 text-green-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-neutral-700">Full business verification completed</span>
              </li>
            </ul>
          </div>

          {/* Redirect Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              Redirecting to your dashboard in 5 seconds...
            </p>
          </div>

          <button
            onClick={handleGoToDashboard}
            className="px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Go to Dashboard Now
          </button>
        </div>
      </div>
    );
  }

  // Incomplete state
  if (status === 'incomplete') {
    const missingRequirements = stripeStatus?.requirements?.currently_due || [];

    return (
      <div className="min-h-screen bg-neutral-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          {/* Warning Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-yellow-100 mb-6">
            <svg
              className="w-12 h-12 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h1 className="text-3xl font-bold text-neutral-900 mb-4">
            Onboarding Incomplete
          </h1>
          <p className="text-lg text-neutral-600 mb-8">
            Please complete all required steps to activate payment processing.
          </p>

          {/* Missing Requirements */}
          {missingRequirements.length > 0 && (
            <div className="bg-white rounded-lg border border-yellow-200 p-6 mb-8 text-left">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Required Information</h3>
              <ul className="space-y-2">
                {missingRequirements.map((req, index) => (
                  <li key={index} className="flex items-start text-neutral-700">
                    <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="capitalize">{req.replace(/_/g, ' ')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleRetryOnboarding}
              disabled={retryLoading}
              className={`px-8 py-3 rounded-lg font-medium text-white transition-all ${
                retryLoading
                  ? 'bg-primary-400 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
              }`}
            >
              {retryLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </span>
              ) : (
                'Resume Onboarding'
              )}
            </button>
            <button
              onClick={handleGoToDashboard}
              className="px-8 py-3 border border-neutral-300 text-neutral-700 hover:bg-neutral-50 font-medium rounded-lg transition-colors focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen bg-neutral-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto text-center">
        {/* Error Icon */}
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-6">
          <svg
            className="w-12 h-12 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>

        <h1 className="text-3xl font-bold text-neutral-900 mb-4">
          Verification Failed
        </h1>
        <p className="text-lg text-neutral-600 mb-8">
          {error || 'We encountered an issue while verifying your payment setup.'}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={checkOnboardingStatus}
            className="px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Try Again
          </button>
          <button
            onClick={handleGoToDashboard}
            className="px-8 py-3 border border-neutral-300 text-neutral-700 hover:bg-neutral-50 font-medium rounded-lg transition-colors focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2"
          >
            Go to Dashboard
          </button>
        </div>

        {/* Support Info */}
        <div className="mt-8 pt-8 border-t border-neutral-200">
          <p className="text-sm text-neutral-600">
            Need help? Contact our support team at{' '}
            <a href="mailto:support@calwmp.com" className="text-primary-600 hover:text-primary-700 font-medium">
              support@calwmp.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export { StripeConnectCallback };
export default StripeConnectCallback;
